import { formatInZone, toZoned } from '@shared/time';
import { intlLocale, t, type Locale } from '@shared/i18n';

/**
 * Every formatter here takes the locale explicitly rather than reading
 * the browser's, because the language on the page is the visitor's
 * CHOICE (or the business's): a Spanish page on an English phone still
 * says "martes".
 */

/** The customer's own zone, as the browser reports it. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function formatMoney(cents: number, currency: string, locale: Locale = 'en'): string {
  try {
    return new Intl.NumberFormat(intlLocale(locale), {
      style: 'currency',
      currency,
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

export function formatDuration(minutes: number, locale: Locale = 'en'): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return t(locale, 'common.duration_minutes', { count: m });
  if (m === 0) return h === 1 ? t(locale, 'common.duration_hour') : t(locale, 'common.duration_hours', { count: h });
  return t(locale, 'common.duration_hours_minutes', { hours: h, minutes: m });
}

/** "2:30 PM" in a zone. */
export function formatTime(zone: string, instant: string | number, locale: Locale = 'en'): string {
  return formatInZone(zone, instant, {
    weekday: undefined, year: undefined, month: undefined, day: undefined, timeZoneName: undefined,
  }, intlLocale(locale));
}

/** "Tuesday, March 10, 2026" for a local YYYY-MM-DD (no zone involved). */
export function formatLocalDate(date: string, options: Intl.DateTimeFormatOptions = {}, locale: Locale = 'en'): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Intl.DateTimeFormat(intlLocale(locale), {
    timeZone: 'UTC', weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', ...options,
  }).format(new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)));
}

/** "Tue, Mar 10, 2026, 2:30 PM CDT" in a zone. */
export function formatDateTime(zone: string, instant: string | number, locale: Locale = 'en'): string {
  return formatInZone(zone, instant, {}, intlLocale(locale));
}

/** Calendar day (YYYY-MM-DD) of an instant in a zone. */
export function dateInZone(zone: string, instant: string | number): string {
  return toZoned(zone, instant).date;
}

/** "America/New_York" -> "America/New York" for reading. */
export function zoneLabel(zone: string): string {
  return zone.replace(/_/g, ' ');
}

/** Short zone name in force at an instant, e.g. "CDT". */
export function zoneAbbrev(zone: string, instant: string | number): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' }).formatToParts(new Date(instant));
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? zone;
  } catch {
    return zone;
  }
}

/** Turns a URL or bare domain into a readable label. */
export function hostOf(url: string): string {
  try {
    return new URL(url.startsWith('http') ? url : `https://${url}`).host.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export function ensureHttp(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}
